/**
 * Individual message components for the chat sidebar.
 *
 * Components:
 * - UserMessage: Displays user input in a right-aligned bubble
 * - SuccessMessage: Shows logged entry with undo option and matching task prompt
 * - ErrorMessage: Shows error with examples and manual form link
 */
import { useState } from 'react';
import { Paper, Text, Group, Button, Stack, ThemeIcon, Loader } from '@mantine/core';
import { IconCheck, IconX, IconChecklist } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoggedEntry, MatchingTestTask } from '@/api/chat';
import { tasksApi } from '@/api/tasks';

interface UserMessageProps {
  content: string;
}

export function UserMessage({ content }: UserMessageProps) {
  return (
    <Paper p="xs" radius="md" bg="blue.0" ml="xl">
      <Text size="sm">{content}</Text>
    </Paper>
  );
}

interface SuccessMessageProps {
  entry: LoggedEntry;
  onUndo: () => void;
  canUndo: boolean;
  undone?: boolean;
  matchingTask?: MatchingTestTask | null;
  tankId?: string;
}

export function SuccessMessage({ entry, onUndo, canUndo, undone, matchingTask, tankId }: SuccessMessageProps) {
  const timeDisplay = formatTime(entry.recorded_at);
  const [taskState, setTaskState] = useState<'prompt' | 'completing' | 'completed' | 'dismissed'>('prompt');
  const queryClient = useQueryClient();

  const handleMarkComplete = async () => {
    if (!matchingTask || !tankId) return;
    setTaskState('completing');
    try {
      await tasksApi.completeInstance(tankId, matchingTask.instance_id, {
        value: entry.value,
        completed_at: entry.recorded_at,
      });
      setTaskState('completed');
      queryClient.invalidateQueries({ queryKey: ['task-instances', tankId] });
      queryClient.invalidateQueries({ queryKey: ['task-definitions', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
    } catch {
      setTaskState('prompt'); // Allow retry
    }
  };

  const dueText = matchingTask
    ? matchingTask.is_overdue
      ? `overdue since ${formatDate(matchingTask.due_at)}`
      : `due ${formatDate(matchingTask.due_at)}`
    : '';

  return (
    <Paper p="sm" radius="md" withBorder style={undone ? { opacity: 0.6 } : undefined}>
      <Group gap="xs" mb={4}>
        <ThemeIcon size="sm" color={undone ? 'gray' : 'green'} variant="light" radius="xl">
          {undone ? <IconX size={14} /> : <IconCheck size={14} />}
        </ThemeIcon>
        <Text size="sm" fw={500} td={undone ? 'line-through' : undefined} c={undone ? 'dimmed' : undefined}>
          Logged {entry.parameter} {entry.value} {entry.unit}
        </Text>
        {undone && (
          <Text size="xs" c="dimmed" fs="italic">
            (undone)
          </Text>
        )}
      </Group>
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed">
          {entry.tank_name} - {timeDisplay}
        </Text>
        {canUndo && !undone && (
          <Button
            size="xs"
            variant="subtle"
            color="red"
            leftSection={<IconX size={12} />}
            onClick={onUndo}
          >
            Undo
          </Button>
        )}
      </Group>

      {/* Matching task prompt */}
      {matchingTask && !undone && taskState === 'prompt' && (
        <Paper p="xs" mt="xs" radius="sm" bg="blue.0">
          <Group gap="xs" mb={4}>
            <IconChecklist size={14} />
            <Text size="xs" fw={500}>
              {matchingTask.task_name} ({dueText})
            </Text>
          </Group>
          {matchingTask.definition_status === 'active' && matchingTask.has_recurrence && matchingTask.next_due_preview && (
            <Text size="xs" c="dimmed" mb={4}>
              Your next test will be scheduled for {formatDate(matchingTask.next_due_preview)}.
            </Text>
          )}
          <Group gap="xs">
            <Button size="xs" variant="filled" onClick={handleMarkComplete}>
              Mark complete
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setTaskState('dismissed')}>
              Leave alone
            </Button>
          </Group>
        </Paper>
      )}

      {matchingTask && taskState === 'completing' && (
        <Group gap="xs" mt="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">Completing task...</Text>
        </Group>
      )}

      {matchingTask && taskState === 'completed' && (
        <Paper p="xs" mt="xs" radius="sm" bg="green.0">
          <Group gap="xs">
            <IconCheck size={14} />
            <Text size="xs" fw={500}>Task completed!</Text>
          </Group>
          {matchingTask.definition_status === 'active' && matchingTask.has_recurrence && matchingTask.next_due_preview && (
            <Text size="xs" c="dimmed">
              Next test scheduled for {formatDate(matchingTask.next_due_preview)}.
            </Text>
          )}
        </Paper>
      )}
    </Paper>
  );
}

interface ErrorMessageProps {
  message: string;
  examples?: string[];
  onManualEntry: () => void;
}

export function ErrorMessage({ message, examples, onManualEntry }: ErrorMessageProps) {
  return (
    <Paper p="sm" radius="md" bg="red.0">
      <Stack gap="xs">
        <Text size="sm">{message}</Text>
        {examples && examples.length > 0 && (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Try formats like:
            </Text>
            {examples.map((ex, i) => (
              <Text key={i} size="xs" c="dimmed" ff="monospace">
                - {ex}
              </Text>
            ))}
          </Stack>
        )}
        <Button size="xs" variant="light" onClick={onManualEntry}>
          Use manual form
        </Button>
      </Stack>
    </Paper>
  );
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();

  if (date.toDateString() === now.toDateString()) {
    return 'just now';
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return 'yesterday';
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
